// ════════════════════════════════════════════════════════════════════════
//  Fassungen: die Geschichte des Lehrgangs
//
//  Diese Seite tut nichts. Sie hieß „Releases“ und trug alles, was mit einem
//  Entwurf geschieht – Anlegen, Speichern, Verwerfen, Veröffentlichen, die
//  Änderungsnotiz und die Hinweisliste. Damit war sie der Ort für Vorgänge, die
//  überall stattfinden, und der Name eines Bereichs, in dem man nichts
//  bearbeitet.
//
//  Die Vorgänge liegen jetzt in der Fassungsleiste, die Notiz im Überblick. Hier
//  bleibt, was hierher gehört: welche Fassungen es gibt.
// ════════════════════════════════════════════════════════════════════════

import { useSession } from "@otto/plattform";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { bundleOf, type Version } from "../lib/data.js";
import { bundleUrl } from "../lib/publish.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./versions.module.css";

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export function Versions() {
  const { client } = useSession();
  const { loading, error, versions, draft } = useDraft();

  if (loading) return <p className={styles.empty}>Fassungen werden geladen …</p>;
  if (error) {
    return (
      <div className={`${styles.notice} ${styles.noticeError}`}>
        Die Fassungen konnten nicht geladen werden.
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>Fassungen</h1>
      <p className={styles.subtitle}>
        Eine veröffentlichte Fassung ist unveränderlich. Klassen hängen an genau einer –
        eine neue erreicht sie erst, wenn die Lehrkraft wechselt.
      </p>

      <table className={styles.wide}>
        <tbody>
          {versions.map((version) => (
            <Row key={version.id} version={version} client={client} />
          ))}
        </tbody>
      </table>

      {!draft && (
        <p className={own.note}>
          Es ist kein Entwurf offen – der Lehrgang steht überall schreibgeschützt. „Neuer
          Entwurf“ oben legt einen an, als Kopie der obersten Fassung.
        </p>
      )}
    </div>
  );
}

function Row({
  version,
  client,
}: {
  version: Version;
  client: ReturnType<typeof useSession>["client"];
}) {
  const checked = bundleOf(version);
  const slug = "bundle" in checked ? checked.bundle.slug : null;

  return (
    <tr>
      <td className={own.number}>
        <strong>Fassung {version.version}</strong>
      </td>
      <td className={own.status}>
        {version.publishedAt ? (
          <span className={own.published}>
            veröffentlicht {dateFormat.format(new Date(version.publishedAt))}
          </span>
        ) : (
          <span className={own.draft}>Entwurf</span>
        )}
      </td>
      <td className={`${own.noteColumn} ${styles.stretch}`}>
        {"problems" in checked ? (
          <span className={own.invalid}>ungültig: {checked.problems[0]?.message}</span>
        ) : (
          (version.notes ?? "")
        )}
      </td>
      <td className={own.right}>
        {version.publishedAt && slug && (
          <a
            href={bundleUrl(client, slug, version.version)}
            target="_blank"
            rel="noreferrer"
          >
            Bundle
          </a>
        )}
      </td>
    </tr>
  );
}
